import { type AttemptFeedback, type PracticeComparison, type Technique } from '@engineer-os/engine';
import { chatUrl } from './chat';
import { readFeedback } from './efficiency';

/** The first three free exercises teach an owner-led comparison. */
export const OPTIMIZATION_MISSION = 'l13-make-the-tests-pass';
export const SNAPSHOT_MISSION = 'l02-first-snapshot';
export const INCIDENT_TRIAL_MISSION = 'trial-incident-reconciliation';
export const INCIDENT_TRANSFER_MISSION = 'trial-incident-transfer';
export const OPTIMIZATION_MISSIONS = ['l00-hello-terminal', SNAPSHOT_MISSION, OPTIMIZATION_MISSION, INCIDENT_TRIAL_MISSION] as const;
export const isOptimizationMission = (id: string): boolean => (OPTIMIZATION_MISSIONS as readonly string[]).includes(id);
export const OPTIMIZATION_CHANGED = 'engineeros:optimization';
export type OptimizationTechnique = Extract<Technique, 'right-size-model' | 'scope-context' | 'restart'>;
const choices = ['right-size-model', 'scope-context', 'restart'];

export type SnapshotCoaching =
  | { kind: 'repeat-git-inspection'; command: 'status' | 'diff'; count: number }
  | { kind: 'no-opportunity' }
  | { kind: 'unavailable' };

type IncidentCoaching = { kind: 'aggregate-in-workspace'; serviceCount: number; rawBytes: number }
  | { kind: 'no-opportunity' | 'unavailable' };

export interface OptimizationSession {
  id: string;
  ref: string;
  phase: 'baseline' | 'review' | 'replay' | 'complete';
  technique: OptimizationTechnique | null;
}

export interface OptimizationStatus {
  session: OptimizationSession | null;
  feedback: AttemptFeedback | null;
  measurement: { state: 'waiting' | 'recording' | 'unmeasured' | 'ready' | 'incompatible'; calls: number };
  /** All baseline retries, not only the passing attempt; null means overflow. */
  baselineModels?: string[] | null;
  coaching?: SnapshotCoaching | IncidentCoaching;
  sameReplayModel?: boolean;
}

const phases = ['baseline', 'review', 'replay', 'complete'];
const measurementStates = ['waiting', 'recording', 'unmeasured', 'ready', 'incompatible'];

function readSnapshotCoaching(raw: unknown): SnapshotCoaching | undefined {
  if (raw === null || typeof raw !== 'object') return;
  const data = raw as Record<string, unknown>;
  if (data.kind === 'unavailable' || data.kind === 'no-opportunity') return { kind: data.kind };
  if (data.kind === 'repeat-git-inspection' && (data.command === 'status' || data.command === 'diff')
    && typeof data.count === 'number' && Number.isSafeInteger(data.count) && data.count >= 2 && data.count <= 200) {
    return { kind: data.kind, command: data.command, count: data.count };
  }
}

function readIncidentCoaching(raw: unknown): IncidentCoaching | undefined {
  if (raw === null || typeof raw !== 'object') return;
  const data = raw as Record<string, unknown>;
  if (data.kind === 'unavailable' || data.kind === 'no-opportunity') return { kind: data.kind };
  if (data.kind === 'aggregate-in-workspace' && typeof data.serviceCount === 'number'
    && Number.isSafeInteger(data.serviceCount) && data.serviceCount >= 4 && data.serviceCount <= 24
    && typeof data.rawBytes === 'number' && Number.isSafeInteger(data.rawBytes) && data.rawBytes > 0) {
    return { kind: data.kind, serviceCount: data.serviceCount, rawBytes: data.rawBytes };
  }
}

/** Never let an older or malformed response become a dollar claim. */
export function readOptimization(raw: unknown, ref: string): OptimizationStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const m = r.measurement as Record<string, unknown> | undefined;
  if (!m || !measurementStates.includes(String(m.state)) || !Number.isSafeInteger(m.calls) || (m.calls as number) < 0) return null;
  if ((m.state === 'ready' || m.state === 'recording') && (m.calls as number) < 1) return null;
  let session: OptimizationSession | null = null;
  if (r.session !== null) {
    if (!r.session || typeof r.session !== 'object') return null;
    const s = r.session as Record<string, unknown>;
    if (typeof s.id !== 'string' || !s.id || s.ref !== ref || !phases.includes(String(s.phase))) return null;
    if (s.technique !== null && !choices.includes(String(s.technique))) return null;
    session = { id: s.id, ref, phase: s.phase as OptimizationSession['phase'], technique: s.technique as OptimizationTechnique | null };
  }
  const feedback = readFeedback(r.feedback);
  if (feedback && feedback.attempt.ref !== ref) return null;
  const status: OptimizationStatus = { session, feedback, measurement: { state: m.state as OptimizationStatus['measurement']['state'], calls: m.calls as number } };
  if (Object.prototype.hasOwnProperty.call(r, 'baselineModels')) {
    if (r.baselineModels === null) status.baselineModels = null;
    else if (Array.isArray(r.baselineModels) && r.baselineModels.length <= 6 && r.baselineModels.every((model) => typeof model === 'string' && /^[a-z0-9~][a-z0-9/._:-]{0,119}$/i.test(model)) && new Set(r.baselineModels).size === r.baselineModels.length) status.baselineModels = r.baselineModels;
    else return null;
  }
  if ((ref === SNAPSHOT_MISSION || ref === INCIDENT_TRIAL_MISSION) && session && session.phase !== 'baseline') {
    status.coaching = (ref === INCIDENT_TRIAL_MISSION ? readIncidentCoaching(r.coaching) : readSnapshotCoaching(r.coaching)) ?? { kind: 'unavailable' };
    if (session.phase === 'complete') status.sameReplayModel = r.sameReplayModel === true;
  }
  return status;
}

export type OptimizationRead = { ok: true; status: OptimizationStatus } | { ok: false; error: 'sign-in' | 'unavailable' | 'refused' };
export type OptimizationAction = { action: 'start' } | { action: 'choose'; sessionId: string; technique: OptimizationTechnique };

export async function optimizationCall(ref: string, action?: OptimizationAction): Promise<OptimizationRead> {
  try {
    const response = await fetch(chatUrl(`/api/ai/optimization?ref=${encodeURIComponent(ref)}`).href, {
      method: action ? 'POST' : 'GET', credentials: 'include', cache: 'no-store',
      ...(action ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ref, ...action }) } : {}),
    });
    if (response.status === 401) return { ok: false, error: 'sign-in' };
    if (!response.ok) return { ok: false, error: response.status === 409 || response.status === 400 ? 'refused' : 'unavailable' };
    const status = readOptimization(await response.json(), ref);
    return status ? { ok: true, status } : { ok: false, error: 'unavailable' };
  } catch {
    return { ok: false, error: 'unavailable' };
  }
}

export interface OptimizationRecord {
  id: string;
  ref: string;
  phase: OptimizationSession['phase'];
  technique: OptimizationTechnique | null;
  fixtureId: string;
  compatible: boolean;
  comparison: PracticeComparison | null;
}
export interface OptimizationHistory {
  records: OptimizationRecord[];
  summary: { comparisons: number; baselineCostMicro: number; replayCostMicro: number; differenceMicro: number; book: string; reported: boolean } | null;
  truncated: boolean;
}
const natural = (n: unknown): n is number => Number.isSafeInteger(n) && (n as number) >= 0;
function readSavedComparison(raw: unknown): PracticeComparison | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.book !== 'string' || !p.book || typeof p.reported !== 'boolean') return null;
  const spend = (raw: unknown) => {
    if (!raw || typeof raw !== 'object') return null;
    const s = raw as Record<string, unknown>;
    return natural(s.costMicro) && natural(s.calls) && s.calls > 0 && natural(s.attempts) && s.attempts > 0 && typeof s.passId === 'string' && s.passId
      ? { costMicro: s.costMicro, calls: s.calls, attempts: s.attempts, passId: s.passId } : null;
  };
  const before = spend(p.before), after = p.after === null ? null : spend(p.after);
  if (!before || before.costMicro <= 0 || (p.after !== null && !after) || (after && after.passId === before.passId)) return null;
  return { book: p.book, reported: p.reported, before, after };
}

/** Invalid or absent summaries never become a client-invented lifetime saving. */
export function readOptimizationHistory(raw: unknown): OptimizationHistory | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.records) || r.records.length > 30 || typeof r.truncated !== 'boolean') return null;
  const records: OptimizationRecord[] = [];
  for (const rawRecord of r.records) {
    if (!rawRecord || typeof rawRecord !== 'object') return null;
    const row = rawRecord as Record<string, unknown>;
    if (typeof row.id !== 'string' || !row.id || typeof row.ref !== 'string' || !isOptimizationMission(row.ref) || !phases.includes(String(row.phase)) || typeof row.fixtureId !== 'string' || typeof row.compatible !== 'boolean' || (row.technique !== null && !choices.includes(String(row.technique)))) return null;
    records.push({ id: row.id, ref: row.ref, phase: row.phase as OptimizationRecord['phase'], technique: row.technique as OptimizationTechnique | null, fixtureId: row.fixtureId, compatible: row.compatible, comparison: readSavedComparison(row.comparison) });
  }
  if (new Set(records.map((row) => row.id)).size !== records.length) return null;
  let summary: OptimizationHistory['summary'] = null;
  if (!r.truncated && r.summary && typeof r.summary === 'object') {
    const s = r.summary as Record<string, unknown>;
    const comparable = records.filter((row) => row.compatible && row.phase === 'complete' && row.comparison?.after);
    if (natural(s.comparisons) && s.comparisons > 0 && s.comparisons === comparable.length && natural(s.baselineCostMicro) && natural(s.replayCostMicro) && Number.isSafeInteger(s.differenceMicro) && typeof s.book === 'string' && s.book && typeof s.reported === 'boolean'
      && comparable.every((row) => row.comparison!.book === s.book && row.comparison!.reported === s.reported)
      && comparable.reduce((n, row) => n + row.comparison!.before.costMicro, 0) === s.baselineCostMicro
      && comparable.reduce((n, row) => n + row.comparison!.after!.costMicro, 0) === s.replayCostMicro
      && s.baselineCostMicro - s.replayCostMicro === s.differenceMicro) {
      summary = { comparisons: s.comparisons, baselineCostMicro: s.baselineCostMicro, replayCostMicro: s.replayCostMicro, differenceMicro: s.differenceMicro as number, book: s.book, reported: s.reported };
    }
  }
  return { records, summary, truncated: r.truncated };
}

export async function optimizationHistoryCall(): Promise<OptimizationHistory | null> {
  try {
    const response = await fetch(chatUrl('/api/ai/optimization/history').href, { credentials: 'include', cache: 'no-store' });
    return response.ok ? readOptimizationHistory(await response.json()) : null;
  } catch { return null; }
}
